import { Box, Button, FormHelperText, Grid, InputLabel, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, FieldValues } from 'react-hook-form';
import useSWR from 'swr';

import { useGetUser } from '../../Hooks/useGetUser';
import {
    CompanyDashboardSelect,
    CompanyListSocial,
    SocialGroupDashboardSelect,
    SocialGroupList,
} from '../../Types/AuthServer';
import { autofillInfoscreen } from '../../Utils/infoscreen/autofillInfoscreen';
import InfoscreenListItem from '../Infoscreen/InfoscreenListItem';
import BasicModal from '../Modals/BasicModal';
import CustomDialog from '../Modals/CustomDialog';
import { BaseInputProps } from './GetFormInput';

export function FormSelectInfoscreen<T extends FieldValues>({
    control,
    label,
    required,
    value,
}: Readonly<BaseInputProps<T>>) {
    const { user } = useGetUser();
    const { data: companyInfoscreens = [] } = useSWR<CompanyDashboardSelect[]>('users/companydashboardselect/');
    const { data: groupInfoscreens = [] } = useSWR<SocialGroupDashboardSelect[]>('users/socialgroupdashboardselect/');
    const [selectedInfoscreen, setSelectedInfoscreen] = useState<
        CompanyDashboardSelect | SocialGroupDashboardSelect | null
    >(null);

    const { data: companyData } = useSWR<CompanyListSocial>(
        selectedInfoscreen?.type === 'Company' &&
            Object.keys(selectedInfoscreen.dashboard).length === 0 &&
            `users/companylistsocial/${selectedInfoscreen.id}/`,
    );

    const { data: groupData } = useSWR<SocialGroupList>(
        selectedInfoscreen?.type === 'Group' &&
            Object.keys(selectedInfoscreen.dashboard).length === 0 &&
            `users/socialgrouplist/${selectedInfoscreen.id}/`,
    );

    const owner = companyData || groupData;

    const [allInfoscreen, setAllInfoscreen] = useState<CompanyDashboardSelect[] | SocialGroupDashboardSelect[]>(() => [
        ...companyInfoscreens.map((i) => ({ ...i, type: 'Company' })),
        ...groupInfoscreens.map((i) => ({ ...i, type: 'Group' })),
    ]);

    useEffect(() => {
        setAllInfoscreen([
            ...companyInfoscreens.map((i) => ({ ...i, type: 'Company' })),
            ...groupInfoscreens.map((i) => ({ ...i, type: 'Group' })),
        ]);
    }, [companyInfoscreens, groupInfoscreens]);

    const filteredInfoscreen =
        user.perm_level === 'superuser'
            ? allInfoscreen
            : allInfoscreen.filter(
                  (i) =>
                      (i.type === 'Company' && user.admin.company.includes(i.id)) ||
                      (i.type === 'Group' && user.admin.social_group.includes(i.id)),
              );

    const [selectModal, setSelectModal] = useState(false);
    const [autofillDialog, setAutofillDialog] = useState(false);
    const handleOpenAutofillDialog = () => {
        setAutofillDialog(true);
    };
    const handleCloseAutofillDialog = () => {
        setAutofillDialog(false);
    };

    const handleOpenSelectModal = () => {
        setSelectModal(true);
    };
    const handleCloseSelectModal = () => {
        setSelectModal(false);
    };

    const handleSelectInfoscreen = (item: CompanyDashboardSelect | SocialGroupDashboardSelect) => {
        if (Object.keys(item.dashboard).length === 0) {
            // Offer autofill if infoscreen is empty
            handleOpenAutofillDialog();
            setSelectedInfoscreen(item);
        } else {
            setSelectedInfoscreen(item);
        }
    };

    const handleConfirmAutofill = () => {
        const updatedInfoscreens = autofillInfoscreen(filteredInfoscreen, selectedInfoscreen?.id ?? '', owner);
        // Update the selected infoscreen with the autofilled data
        const updatedInfoscreen = updatedInfoscreens.find((i) => i.id === selectedInfoscreen?.id);
        if (updatedInfoscreen) {
            setSelectedInfoscreen(updatedInfoscreen);
        }
        // Update the state with the new infoscreens
        setAllInfoscreen(updatedInfoscreens);
        handleCloseAutofillDialog();
    };

    return (
        <Controller
            control={control}
            name={value}
            render={({ field: { onChange }, fieldState: { error } }) => {
                return (
                    <Box>
                        <InputLabel sx={{ color: 'black', fontWeight: 'medium', mb: 1 }}>{label}</InputLabel>
                        <Button onClick={handleOpenSelectModal} size="small" variant="contained">
                            Select
                        </Button>
                        <FormHelperText sx={{ color: error?.message ? 'error.main' : 'initial' }}>
                            {error?.message}
                        </FormHelperText>
                        <BasicModal handleClose={handleCloseSelectModal} open={selectModal} title="Select infoscreen">
                            <Stack
                                sx={{
                                    backgroundColor: '#edf2f6',
                                    height: '75vh',
                                    overflow: 'auto',
                                    p: 2,
                                    width: { sm: 1200, xs: '100dvw' },
                                }}
                            >
                                <Grid
                                    container
                                    spacing={2}
                                    sx={{
                                        mb: 2,
                                    }}
                                >
                                    {filteredInfoscreen.map((item) => (
                                        <Grid
                                            key={item.id}
                                            onClick={() => handleSelectInfoscreen(item)}
                                            size={{ sm: 6, xs: 12 }}
                                        >
                                            <InfoscreenListItem
                                                infoscreen={item}
                                                selected={item.id === selectedInfoscreen?.id}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Stack>
                            <Stack
                                sx={{
                                    alignItems: 'flex-end',
                                    height: 56,
                                    justifyContent: 'center',
                                    p: 2,
                                }}
                            >
                                <Button
                                    disabled={!selectedInfoscreen}
                                    onClick={() => {
                                        onChange(selectedInfoscreen);
                                        handleCloseSelectModal();
                                    }}
                                    variant="contained"
                                >
                                    Select
                                </Button>
                            </Stack>
                        </BasicModal>
                        <CustomDialog
                            content={{
                                body: 'This infoscreen is empty. Do you want to autofill it?',
                                title: 'Autofill',
                            }}
                            onClose={handleCloseAutofillDialog}
                            onConfirm={handleConfirmAutofill}
                            open={autofillDialog}
                        />
                    </Box>
                );
            }}
            rules={{
                required: required ? `Infoscreen cannot be empty` : false,
            }}
        />
    );
}
